<script setup lang="ts">
    import { onMounted, reactive, ref, toRaw } from 'vue';
    import axios from 'axios';
    import { useRoute, useRouter } from 'vue-router';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import MarketProduitFormGeneralView from './views/MarketProduitFormGeneralView.vue';
    import MarketProduitFormPricesView from './views/MarketProduitFormPricesView.vue';
    import MarketProduitFormAttributsView from './views/MarketProduitFormAttributsView.vue';
    import MarketProduitFormPromotionView from './views/MarketProduitFormPromotionView.vue';

    const router = useRouter();
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const isFormValid = ref(false);
    const formData = reactive({
        id: "",
        image: null as any,
        image_preview: '/assets/users/default.png',
    });
    const generalFormData = ref({
        categorie_ids: [] as any[],
        sku: "",
        ean: "",
        nom: "",
        description: "",
        montant: 0,
        metadata: [] as any[],
        fournisseurs_ids: [] as any[],
        quantite: 0,
    });
    const echeancesFormData = ref({
        echeances: [] as any[],
        mode_paiement: 'cash',
    });
    const promotionFormData = ref({
        type_promotion: null as string | null,
        taux_promotion: 0
    });
    const attributsFormData = reactive({
        attributs: [] as any[],
        produit_attributs: [] as any[],
    });
    const errors = reactive({
        image: "",
    });
    const startupFormDatas = reactive({
        attributs: [] as any[],
        categories: [] as any[],
        fournisseurs: [] as any[],
        echeances: [] as any[],
        types_promotion: [] as any[],
        produit: {} as any,
        market_echeances_time: -1,
        market_delivery_time: -1,
    });
    const tabs = ['Infos Produit', 'Tarifs & Paiement', 'Promotion', 'Attributs'];
    const activeTab = ref('Infos Produit');

    onMounted(() => {
        const form_datas: any = route.meta.form_datas;
        Object.assign(startupFormDatas, {
            attributs: form_datas?.attributs || [],
            categories: form_datas?.categories || [],
            fournisseurs: form_datas?.fournisseurs || [],
            echeances: form_datas?.echeances || [],
            types_promotion: form_datas?.types_promotion || [],
            produit: form_datas?.produit || {},
            market_echeances_time: form_datas?.market_echeances_time || -1,
            market_delivery_time: form_datas?.market_delivery_time || -1,
        });
        if (form_datas?.produit) {
            const item = form_datas.produit as any;
            formData.id = item.id;
            if (item.image !== undefined) {
                formData.image_preview = item.image_url;
            }

            generalFormData.value = {
                categorie_ids: item.categorie_ids || [],
                sku: item.sku || "",
                ean: item.ean || "",
                nom: item.nom || "",
                description: item.description || "",
                montant: item.montant || 0,
                metadata: item.metadata || [],
                fournisseurs_ids: item.fournisseurs_ids || [],
                quantite: item.quantite || 0,
            };

            echeancesFormData.value = {
                echeances: item.echeances || [],
                mode_paiement: item.mode_paiement || 'cash',
            };

            promotionFormData.value = {
                type_promotion: item.type_promotion || null,
                taux_promotion: item.taux_promotion || 0,
            };

            Object.assign(attributsFormData, {
                attributs: item.attributs || [],
                produit_attributs: item.produit_attributs || [],
            });
        }
    });

    function validate() {
        let isValid = true;
        
        isFormValid.value = isValid;

        return isValid;
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            const formToUpload = new FormData();
            if (formData.id != null) {
                formToUpload.append("id", `${formData.id}`);
            }
            if (formData.image?.file) {
                formToUpload.append("image", formData.image.file, formData.image.name);
            }
            
            formToUpload.append("categorie_ids", generalFormData.value.categorie_ids ? JSON.stringify(generalFormData.value.categorie_ids) : '[]');
            formToUpload.append("sku", generalFormData.value.sku);
            formToUpload.append("ean", generalFormData.value.ean);
            formToUpload.append("nom", generalFormData.value.nom);
            formToUpload.append("quantite", generalFormData.value.quantite.toString());
            formToUpload.append("description", generalFormData.value.description);
            formToUpload.append("montant", generalFormData.value.montant.toString());
            formToUpload.append("metadata", generalFormData.value.metadata ? JSON.stringify(generalFormData.value.metadata) : '[]');
            formToUpload.append("fournisseurs_ids", generalFormData.value.fournisseurs_ids ? JSON.stringify(generalFormData.value.fournisseurs_ids) : '[]');

            formToUpload.append("type_promotion", promotionFormData.value.type_promotion ? promotionFormData.value.type_promotion : '');
            formToUpload.append("taux_promotion", promotionFormData.value.taux_promotion.toString());

            formToUpload.append("echeances", echeancesFormData.value.echeances ? JSON.stringify(echeancesFormData.value.echeances) : '[]');
            formToUpload.append("mode_paiement", echeancesFormData.value.mode_paiement ? echeancesFormData.value.mode_paiement : 'cash');

            formToUpload.append("attributs", attributsFormData.attributs ? JSON.stringify(attributsFormData.attributs.map(a => a.id)) : '[]');

            attributsFormData.produit_attributs.forEach((produit_attribut: any, index: number) => {
                if (produit_attribut.image?.file) {
                    formToUpload.append(`produit_attributs[${index}][image]`, produit_attribut.image.file, produit_attribut.image.name);
                } else {
                    formToUpload.append(`produit_attributs[${index}][image]`, '');
                }
                formToUpload.append(`produit_attributs[${index}][guid]`, produit_attribut.guid);
                formToUpload.append(`produit_attributs[${index}][montant]`, produit_attribut.montant.toString());
                formToUpload.append(`produit_attributs[${index}][quantite]`, produit_attribut.quantite.toString());
                formToUpload.append(`produit_attributs[${index}][produit_attributs]`, produit_attribut.produit_attributs ? JSON.stringify(produit_attribut.produit_attributs) : '[]');
            });

            await axios.post(`/backoffice/market-produits/save`, formToUpload);
            router.push('/app/market/market-produits/list');
        } catch (error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

    function onFileImageUploaded(uploadedFile: { file: any, file_name: string, file_preview: string }) {
        formData.image_preview = uploadedFile.file_preview;
        formData.image = { file: uploadedFile.file, name: uploadedFile.file_name };
    }
</script>

<style scoped>
.tabs {
  display: flex;
  gap: 10px;
}

.tabs button {
  padding: 10px;
  border: none;
  background: #eee;
  cursor: pointer;
  flex: 1;
}

.tabs button.active {
  background: #009b72;
  color: white;
}
</style>

<template>
    <section class="container-cash-1 bg-grey-50">
        <div class="page-space-30"></div>

        <section class="s15_mobile px-4">
            <div class="widget px-7 py-7">
                <h4 class="font-semibold mb-5">Produits Market / Edition <span class="text-sm text-grey-500"></span></h4>

                <div class="tabs">
                    <button v-for="tab in tabs" :key="tab" :class="{ active: activeTab === tab }"
                        @click="activeTab = tab">
                        {{ tab }}
                    </button>
                </div>

                <div class="tab-content">
                    <div v-show="activeTab === 'Infos Produit'">
                        <market-produit-form-general-view :startupFormDatas="startupFormDatas" v-model="generalFormData" />
                    </div>
                    <div v-show="activeTab === 'Tarifs & Paiement'">
                        <market-produit-form-prices-view :startupFormDatas="startupFormDatas" v-model="echeancesFormData" />
                    </div>
                    <div v-show="activeTab === 'Promotion'">
                        <market-produit-form-promotion-view :startupFormDatas="startupFormDatas" v-model="promotionFormData" />
                    </div>
                    <div v-show="activeTab === 'Attributs'">
                        <market-produit-form-attributs-view :startupFormDatas="startupFormDatas" v-model="attributsFormData" :imagePreview="formData.image_preview" />
                    </div>
                </div>

                <div class="clear-both h-30"></div>
                <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                <p class="text-text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                <div class="flex gap-2">
                    <custom-button id="btn-submit" type="button" :classesList="'btn lg w-100'" :loading="loading" @click="onSubmit()">
                        <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                        <span>Enregistrer</span>
                    </custom-button>
                </div>

            </div>
        </section>
        <section class="s5_mobile px-4">
            <div class="widget px-7 py-7">
                <h4 class="font-semibold mb-5 text-center">Image <span class="text-sm text-grey-500"></span></h4>
                <div class="custom-upload-size margin-auto flex mb-8">
                    <custom-upload-input id="image" label="Image" type="IMAGE" :preview="formData.image_preview" @uploaded="onFileImageUploaded"></custom-upload-input>
                </div>
            </div>
        </section>
    </section>
</template>
